import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { generatePersonalityReport, getCachedReport, getPremiumStatus, getEntitlements } from '../../lib/api';
import { SKETCH_PATTERNS } from '../../lib/images';
import useRazorpay from '../../hooks/useRazorpay';
import useCurrency from '../../hooks/useCurrency';
import usePromoPreview from '../../hooks/usePromoPreview';
import PriceWithPromo from '../PriceWithPromo';
import InlineContactFields from '../purchase/InlineContactFields';
import { captureContact } from '../../lib/api';
import useContactAutoCapture from '../../hooks/useContactAutoCapture';
import { downloadElementAsPdf } from '../../lib/pdf';
import { trackEvent } from '../../lib/firebase';
import {
  Sparkles, RefreshCw, Share2, Loader2, FileText, Brain,
  Target, TrendingUp, Heart, Briefcase, Users, Lightbulb,
  Zap, BookOpen, Lock, Crown, Wind, Download
} from 'lucide-react';

export default function PersonalityResult({ result, onRetake }) {
  const { user, deviceId, setUserDirect } = useAuth();
  const { pay, loading: payLoading, error: payError, authDialog } = useRazorpay();
  const currency = useCurrency();
  const promo = usePromoPreview();
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportRef = useRef(null);
  const [isPremium, setIsPremium] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [contactPhone, setContactPhone] = useState('');
  const [contactError, setContactError] = useState('');
  // Two-step paywall: contact → reveal price.
  // The price/Unlock CTA stay hidden until the user submits their email
  // (and optional phone), at which point we save to the DB and reveal pricing.
  const [revealedPrice, setRevealedPrice] = useState(!!user?.email);
  const [revealing, setRevealing] = useState(false);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((contactEmail || '').trim());

  // Robust auto-capture: fires on mount (if user already has email),
  // on email-input blur (when valid), and as sendBeacon on tab close.
  // Backstop in case the user types a valid email but never clicks "Unlock".
  const autoCapture = useContactAutoCapture({
    deviceId,
    email: contactEmail,
    phone: contactPhone,
    source: 'personality',
  });

  const handleRevealPrice = async () => {
    const trimmedEmail = (contactEmail || '').trim().toLowerCase();
    const trimmedPhone = (contactPhone || '').trim();
    if (!emailIsValid) {
      setContactError('Please enter a valid email.');
      return;
    }
    setContactError('');
    setRevealing(true);
    try {
      await captureContact({
        device_id: deviceId,
        email: trimmedEmail,
        phone: trimmedPhone || null,
        source: 'personality',
      });
      try {
        setUserDirect({ ...(user || {}), email: trimmedEmail, guest: user?.guest !== false });
      } catch {}
      setRevealedPrice(true);
    } catch (err) {
      setContactError(err?.response?.data?.detail || 'Could not save contact. Please try again.');
    } finally {
      setRevealing(false);
    }
  };

  useEffect(() => {
    async function init() {
      try {
        const [premRes, entRes] = await Promise.allSettled([
          getPremiumStatus(deviceId),
          getEntitlements(deviceId, user?.email),
        ]);
        const premFromRemote = premRes.status === 'fulfilled' && premRes.value.data?.is_premium;
        const ent = entRes.status === 'fulfilled' ? (entRes.value.data || {}) : {};
        if (premFromRemote || ent.premium) setIsPremium(true);
        if (premFromRemote || ent.premium || ent.personality_report) setUnlocked(true);
      } catch {}
      // Check for cached report
      try {
        const res = await getCachedReport(deviceId);
        if (res.data?.success && res.data?.report) {
          setReport(res.data.report);
          setShowReport(true);
        }
      } catch {}
    }
    init();
  }, [deviceId]);

  const reportUnlocked = result?.report_unlocked || isPremium || unlocked;

  const handlePurchase = () => {
    const trimmedEmail = (contactEmail || '').trim().toLowerCase();
    const trimmedPhone = (contactPhone || '').trim();
    if (!emailIsValid) {
      setContactError('Please enter a valid email.');
      return;
    }
    setContactError('');
    // Contact was already saved when the user clicked "Reveal price". No-op
    // for `captureContact` here — but re-run it as a safety net in case the
    // user typed a different email after revealing (e.g. fixed a typo).
    captureContact({
      device_id: deviceId,
      email: trimmedEmail,
      phone: trimmedPhone || null,
      source: 'personality',
    }).catch(() => { /* non-blocking */ });
    pay({
      deviceId,
      itemType: 'personality_report',
      amount: result?.report_price || 399,
      currency: currency.currency,
      userName: user?.name,
      userEmail: trimmedEmail,
      userContact: trimmedPhone || undefined,
      allowGuest: true,
      onSuccess: () => {
        setUnlocked(true);
      },
    });
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await generatePersonalityReport({
        device_id: deviceId,
        user_name: user?.name || 'User',
      });
      if (res.data?.success && res.data?.report) {
        setReport(res.data.report);
        setShowReport(true);
      } else {
        setError('Failed to generate report. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate report.');
    }
    setGenerating(false);
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    setPdfLoading(true);
    try {
      const nm = user?.name ? `personality-report-${user.name.split(' ')[0]}` : 'personality-report';
      await downloadElementAsPdf(reportRef.current, nm);
      trackEvent('personality_report_download_pdf', { archetype: result?.archetype_id });
    } catch (e) {
      console.error('PDF export failed', e);
    } finally {
      setPdfLoading(false);
    }
  };

  if (!result) return null;

  const archetypeName = result.archetype_name || result.archetype_id || 'Your Archetype';
  const emoji = result.archetype_emoji || '';
  const tagline = result.archetype_tagline || '';
  const insights = result.preview_insights || [];
  const moodPreview = result.mood_preview || '';

  return (
    <div className="max-w-3xl mx-auto" data-testid="personality-result">
      {authDialog}
      {/* LOCKED STATE — paywall on top, blurred heading + preview */}
      {!reportUnlocked && (
        <>
          {/* Paywall CTA — on top.
              Price + Unlock button stay hidden until a valid email is entered.
              Phone remains optional. */}
          <div className="rounded-xl p-8 border text-center mb-8 animate-fade-in"
            style={{ background: 'linear-gradient(135deg, #879B7D08 0%, #E2725B08 100%)', borderColor: 'var(--accent)' + '50' }}
            data-testid="personality-paywall">
            <Lock className="w-10 h-10 mx-auto mb-4" style={{ color: 'var(--accent)' }} />
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
              Unlock Your Full Results
            </h3>
            <p className="text-sm mb-5 max-w-md mx-auto leading-relaxed" style={{ color: 'var(--muted-fg)' }}>
              Get your complete personality profile, strengths, growth areas, cognitive patterns, real-life impact analysis, and personalized recommendations — all from a programmatic, scientific analysis of your responses.
            </p>
            <InlineContactFields
              tone="theme"
              email={contactEmail}
              phone={contactPhone}
              onEmailChange={(v) => { setContactEmail(v); if (revealedPrice) setRevealedPrice(false); }}
              onPhoneChange={setContactPhone}
              onEmailBlur={autoCapture.onEmailBlur}
              error={contactError}
            />
            {revealedPrice ? (
              <div className="animate-fade-in" data-testid="personality-price-block">
                <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                  <PriceWithPromo inrAmount={result.report_price || 399} currency={currency} promo={promo} size="xl" testid="personality-price" />
                  <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--primary)' + '15', color: 'var(--primary)' }}>one-time</span>
                </div>
                {!currency.isINR && !currency.loading && (
                  <p className="text-xs mb-3" style={{ color: 'var(--muted-fg)' }}>
                    Billed in {currency.currency} · INR ₹{promo.valid ? Math.round(promo.apply(result.report_price || 399)) : (result.report_price || 399)}
                  </p>
                )}
                {payError && <p className="text-xs mb-3" style={{ color: '#DC2626' }}>{payError}</p>}
                <button onClick={handlePurchase} disabled={payLoading} data-testid="buy-personality-btn"
                  className="px-10 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 inline-flex items-center gap-2"
                  style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                  {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  {payLoading ? 'Processing...' : `Unlock for ${currency.format(promo.valid ? promo.apply(result.report_price || 399) : (result.report_price || 399))}`}
                </button>
                <p className="text-xs mt-3" style={{ color: 'var(--muted-fg)' }}>
                  Or get everything with <span className="font-semibold" style={{ color: 'var(--primary)' }}>Premium</span>
                </p>
              </div>
            ) : (
              <button
                onClick={handleRevealPrice}
                disabled={!emailIsValid || revealing}
                data-testid="personality-reveal-price-btn"
                className="px-10 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                {revealing && <Loader2 className="w-4 h-4 animate-spin" />}
                {revealing ? 'Saving…' : 'Unlock My Results'}
              </button>
            )}
          </div>

          {/* Blurred Archetype Heading */}
          <div className="rounded-2xl overflow-hidden mb-8 relative"
            style={{ filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none', background: 'linear-gradient(135deg, #879B7D 0%, #768A6C 40%, #2C4A3B 100%)' }}>
            <div className="absolute inset-0 opacity-8"
              style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '300px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
            <div className="relative z-10 py-14 px-8 text-center">
              <p className="text-xs uppercase tracking-widest text-white/60 mb-4" style={{ fontFamily: 'Manrope' }}>Your Wellness Archetype</p>
              <div className="text-6xl mb-4">{emoji}</div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>{archetypeName}</h1>
              <p className="text-white/80 text-base max-w-md mx-auto leading-relaxed">{tagline}</p>
            </div>
          </div>

          {/* Blurred Quick Insights */}
          <div className="relative mb-6" data-testid="locked-insights">
            <div className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)', filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Your Key Traits</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {(insights.length > 0 ? insights : ['Deep thinker', 'Pattern seeker', 'Emotionally aware']).map((insight, i) => (
                  <span key={i} className="px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ background: 'var(--primary)' + '15', color: 'var(--primary)' }}>
                    {insight}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Blurred Mood Preview */}
          <div className="relative mb-6">
            <div className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)', filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
              <h3 className="font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Your Mood Operating System</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>
                {moodPreview || 'Your emotional responses follow predictable patterns based on your unique neural wiring. Understanding these patterns is the first step to mastering your emotional landscape.'}
              </p>
            </div>
          </div>

          {/* Blurred Report Sections Preview */}
          <div className="relative mb-6">
            <div className="space-y-4" style={{ filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none' }}>
              {['Core Personality Profile', 'Your Strengths', 'Growth Areas'].map((title, i) => (
                <div key={i} className="rounded-lg p-6 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                    <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{title}</h3>
                  </div>
                  <div className="h-3 rounded bg-gray-200 mb-2 w-3/4" />
                  <div className="h-3 rounded bg-gray-200 mb-2 w-full" />
                  <div className="h-3 rounded bg-gray-200 w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* UNLOCKED — Archetype Card visible */}
      {reportUnlocked && (
        <div className="rounded-2xl overflow-hidden mb-8 relative animate-slide-up"
          style={{ background: 'linear-gradient(135deg, #879B7D 0%, #768A6C 40%, #2C4A3B 100%)' }}>
          <div className="absolute inset-0 opacity-8"
            style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '300px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
          <div className="relative z-10 py-14 px-8 text-center">
            <p className="text-xs uppercase tracking-widest text-white/60 mb-4" style={{ fontFamily: 'Manrope' }}>Your Wellness Archetype</p>
            <div className="text-6xl mb-4">{emoji}</div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }} data-testid="archetype-name">{archetypeName}</h1>
            <p className="text-white/80 text-base max-w-md mx-auto leading-relaxed">{tagline}</p>
          </div>
        </div>
      )}

      {/* UNLOCKED STATE */}
      {reportUnlocked && (
        <>
          {/* Quick Insights */}
          {insights.length > 0 && (
            <div className="rounded-lg p-6 border mb-6 animate-fade-in stagger-1"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="personality-insights">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Your Key Traits</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {insights.map((insight, i) => (
                  <span key={i} className="px-4 py-2 rounded-lg text-sm font-medium"
                    style={{ background: 'var(--primary)' + '15', color: 'var(--primary)', border: '1px solid var(--primary)' + '30' }}>
                    {insight}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Mood Preview */}
          {moodPreview && (
            <div className="rounded-lg p-6 border mb-6 animate-fade-in stagger-2"
              style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
              <h3 className="font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Your Mood Operating System</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{moodPreview}</p>
            </div>
          )}

          {/* Generate Full Report CTA */}
          {!showReport && (
            <div className="rounded-lg p-6 border mb-6 animate-fade-in text-center"
              style={{ background: 'linear-gradient(135deg, var(--surface) 0%, #F2EFE9 100%)', borderColor: 'var(--primary)' + '40' }}
              data-testid="generate-report-section">
              <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--primary)' }} />
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                Generate Your Full AI Report
              </h3>
              <p className="text-sm mb-5 max-w-md mx-auto" style={{ color: 'var(--muted-fg)' }}>
                Get a deeply personalized analysis of your personality, cognitive patterns, strengths, growth areas, and actionable recommendations.
              </p>
              {error && <p className="text-sm mb-3" style={{ color: '#DC2626' }}>{error}</p>}
              <button onClick={handleGenerateReport} disabled={generating} data-testid="generate-report-btn"
                className="px-8 py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 inline-flex items-center gap-2"
                style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate My Report</>}
              </button>
            </div>
          )}

          {/* Full Report */}
          {showReport && report && (
            <div ref={reportRef} className="space-y-5 animate-fade-in" data-testid="full-report">
              <ReportSection icon={Brain} title="Headline Insight" color="var(--primary)">
                <p className="text-base leading-relaxed font-medium" style={{ color: 'var(--text)' }}>{report.headline}</p>
              </ReportSection>

              <ReportSection icon={Target} title="Core Personality Profile" color="var(--primary)">
                <div className="space-y-4">
                  {report.core_profile && Object.entries(report.core_profile).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs uppercase tracking-widest font-semibold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--muted-fg)' }}>
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{value}</p>
                    </div>
                  ))}
                </div>
              </ReportSection>

              <ReportSection icon={TrendingUp} title="Your Strengths" color="#34A853">
                <div className="space-y-4">
                  {(report.strengths || []).map((s, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
                        style={{ background: '#34A85315', color: '#34A853' }}>{i + 1}</div>
                      <div>
                        <p className="font-bold text-sm mb-0.5" style={{ color: 'var(--text)' }}>{s.title}</p>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{s.explanation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ReportSection>

              <ReportSection icon={Lightbulb} title="Growth Areas" color="var(--accent)">
                <div className="space-y-4">
                  {(report.growth_areas || []).map((g, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
                        style={{ background: 'var(--accent)' + '15', color: 'var(--accent)' }}>{i + 1}</div>
                      <div>
                        <p className="font-bold text-sm mb-0.5" style={{ color: 'var(--text)' }}>{g.title}</p>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{g.explanation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ReportSection>

              {report.cognitive_patterns && (
                <ReportSection icon={Zap} title="Cognitive & Emotional Patterns" color="#9B59B6">
                  <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ fontFamily: 'Manrope', color: '#9B59B6' }}>
                    {report.cognitive_patterns.primary_pattern}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{report.cognitive_patterns.details}</p>
                </ReportSection>
              )}

              {report.real_life_impact && (
                <ReportSection icon={Heart} title="Real-Life Impact" color="#E91E63">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { icon: Briefcase, label: 'Work', text: report.real_life_impact.work },
                      { icon: Users, label: 'Relationships', text: report.real_life_impact.relationships },
                      { icon: Heart, label: 'Personal Habits', text: report.real_life_impact.personal_habits },
                    ].map((item, i) => (
                      <div key={i} className="rounded-lg p-4" style={{ background: 'var(--bg)' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <item.icon className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                          <p className="text-xs font-bold uppercase tracking-widest" style={{ fontFamily: 'Manrope', color: 'var(--muted-fg)' }}>{item.label}</p>
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{item.text}</p>
                      </div>
                    ))}
                  </div>
                </ReportSection>
              )}

              <ReportSection icon={Lightbulb} title="Personalized Recommendations" color="var(--primary)">
                <div className="space-y-3">
                  {(report.recommendations || []).map((r, i) => (
                    <div key={i} className="rounded-lg p-4 flex gap-3" style={{ background: 'var(--bg)' }}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold"
                        style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>{i + 1}</div>
                      <div>
                        <p className="font-bold text-sm mb-0.5" style={{ color: 'var(--text)' }}>{r.action}</p>
                        <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{r.why}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ReportSection>

              {report.suggested_program && (
                <div className="rounded-lg p-6 border animate-fade-in"
                  style={{ background: 'linear-gradient(135deg, #879B7D10 0%, #E2725B10 100%)', borderColor: 'var(--primary)' + '40' }}
                  data-testid="suggested-program">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                    <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Suggested Program</h3>
                  </div>
                  <h4 className="text-lg font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--primary)' }}>
                    {report.suggested_program.name}
                  </h4>
                  {report.suggested_program.duration && (
                    <p className="text-xs mb-2" style={{ color: 'var(--muted-fg)' }}>{report.suggested_program.duration}</p>
                  )}
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text)' }}>{report.suggested_program.description}</p>
                </div>
              )}

              {report.closing_insight && (
                <div className="rounded-lg p-6 border text-center animate-fade-in"
                  style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="closing-insight">
                  <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: 'var(--accent)' }} />
                  <p className="text-base leading-relaxed italic" style={{ color: 'var(--text)' }}>
                    "{report.closing_insight}"
                  </p>
                </div>
              )}

              <div className="text-center pt-2" data-pdf-ignore="true">
                <button onClick={() => { setReport(null); setShowReport(false); }}
                  className="text-xs underline" style={{ color: 'var(--muted-fg)' }} data-testid="regenerate-report-btn">
                  Regenerate report
                </button>
              </div>
            </div>
          )}

          {/* Download PDF (unlocked + report generated) */}
          {showReport && report && (
            <div className="mt-6 flex justify-center animate-fade-in" data-pdf-ignore="true">
              <button onClick={handleDownloadPdf} disabled={pdfLoading} data-testid="download-personality-pdf"
                className="px-6 py-3 rounded-lg font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center gap-2"
                style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
                {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {pdfLoading ? 'Preparing PDF…' : 'Download full report as PDF'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Bottom Actions */}
      <div className="flex gap-3 mt-8 animate-fade-in">
        <button onClick={onRetake} data-testid="retake-test"
          className="flex-1 py-3 rounded-lg font-medium text-sm transition-all hover:opacity-80 flex items-center justify-center gap-2"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          <RefreshCw className="w-4 h-4" /> Retake Test
        </button>
        <button onClick={() => {
          if (navigator.share) {
            navigator.share({ title: `I'm ${archetypeName}!`, text: `I discovered my wellness archetype on Today: ${archetypeName} - ${tagline}` });
          }
        }} data-testid="share-result"
          className="flex-1 py-3 rounded-lg font-medium text-sm transition-all hover:opacity-90 flex items-center justify-center gap-2"
          style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
          <Share2 className="w-4 h-4" /> Share Result
        </button>
      </div>
    </div>
  );
}

function ReportSection({ icon: Icon, title, color, children }) {
  return (
    <div className="rounded-lg p-6 border animate-fade-in"
      style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5" style={{ color }} />
        <h3 className="font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}
